/**
 * FileDetailsDialog
 *
 * Read-only modal that surfaces file metadata (name, MIME type, size,
 * creation date, owner) plus a single Download CTA. Used as the fallback
 * surface for files that have no in-browser viewer, and as the "View
 * Details" entry point from the actions menu.
 *
 * Icon resolution and date formatting are passed in from the page so
 * this stays a pure presentational component.
 */

import { type FileItem } from "@crm-src/lib/files-api"
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Typography,
} from "@mui/material"
import { FileText, Archive, HardDrive, Calendar, User, Download } from "lucide-react"
import { FileTypeIcon } from "./file-type-icon"
import { formatFileDate } from "./format"

interface FileDetailsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  selectedFile: FileItem | null
  onDownload: (file: FileItem) => void
}

export function FileDetailsDialog({
  open,
  onOpenChange,
  selectedFile,
  onDownload,
}: FileDetailsDialogProps) {
  const iconSx = { color: "text.secondary", display: "inline-flex", flexShrink: 0 }

  return (
    <Dialog open={open} onClose={() => onOpenChange(false)} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1 }}>
        {selectedFile && <FileTypeIcon file={selectedFile} />}
        File Details
      </DialogTitle>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Information about "{selectedFile?.name}"
        </DialogContentText>

        {selectedFile && (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={iconSx}>
                  <FileText size={16} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Name:</Typography>
                <Typography variant="body2">{selectedFile.name}</Typography>
              </Box>

              {selectedFile.content_type && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={iconSx}>
                    <Archive size={16} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Type:</Typography>
                  <Typography variant="body2">{selectedFile.content_type}</Typography>
                </Box>
              )}

              {selectedFile.file_size_formatted && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box component="span" sx={iconSx}>
                    <HardDrive size={16} />
                  </Box>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>Size:</Typography>
                  <Typography variant="body2">{selectedFile.file_size_formatted}</Typography>
                </Box>
              )}

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={iconSx}>
                  <Calendar size={16} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Created:</Typography>
                <Typography variant="body2">{formatFileDate(selectedFile.created_at)}</Typography>
              </Box>

              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box component="span" sx={iconSx}>
                  <User size={16} />
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>Created by:</Typography>
                <Typography variant="body2">{selectedFile.created_by?.name || 'Unknown User'}</Typography>
              </Box>
            </Box>

            <Box sx={{ display: "flex", gap: 1, pt: 2 }}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<Download size={16} />}
                onClick={() => {
                  onDownload(selectedFile)
                  onOpenChange(false)
                }}
              >
                Download
              </Button>
            </Box>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  )
}
